import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { environment } from "src/environments/environment";

/** Backend wraps 2xx JSON as `{ success, message, errors, data }`; return the `data` part. */
function unwrap<T>(res: unknown): T {
  if (res && typeof res === "object" && "data" in res && "success" in res) {
    return (res as { data: T }).data;
  }
  return res as T;
}

/**
 * Trạng thái của lần thử thanh toán gần nhất, khớp với PaymentTransactionStatus phía backend.
 *
 * `UnderReview` và `Reversed` là các trường hợp tiền của khách đã bị trừ nhưng giao dịch chưa sạch
 * sẽ (VNPay nghi ngờ gian lận, hoặc giao dịch bị đảo). Không được hiển thị như thành công, cũng
 * không được hiển thị như thất bại.
 */
export type PaymentTransactionStatus =
  | "Pending"
  | "Success"
  | "Failed"
  | "UnderReview"
  | "Reversed"
  | "Expired";

export interface CreatePaymentResponse {
  success: boolean;
  paymentUrl: string;
  message: string;
  orderId: string;
  /** Mã giao dịch gửi sang VNPay cho lần thử này. Mỗi lần thử là một mã khác nhau. */
  txnRef: string;
  expiresAt?: string;
}

export interface PaymentSyncResult {
  orderId: string;
  orderCode?: string;
  outcome: string;
  message: string;
  paymentStatus?: string;
  transactionStatus?: PaymentTransactionStatus;
  isCompleted: boolean;
}

export interface PaymentStatusResponse {
  orderId: string;
  orderCode: string;
  /** Trạng thái thanh toán của đơn hàng: Unpaid, PartiallyPaid, Paid, Refunded, Failed, NotFound. */
  paymentStatus: string;
  transactionStatus: PaymentTransactionStatus;
  txnRef?: string;
  transactionId?: string;
  amount?: number;
  paymentDate?: string;
  expiresAt?: string;
  /** true khi đã chốt hẳn - không cần hỏi lại nữa. */
  isCompleted: boolean;
}

/**
 * Trạng thái gộp của lệnh hoàn tiền mà khách nhìn thấy. Backend gộp các trạng thái nội bộ
 * (Sent / NeedsReconcile...) thành "Processing" - với khách, tất cả đều là "đang xử lý".
 */
export type RefundPublicStatus =
  | "Requested"
  | "Processing"
  | "Succeeded"
  | "Failed"
  | "Cancelled";

export interface RefundStatusItem {
  status: RefundPublicStatus;
  statusText: string;
  amount: number;
  requestedAt?: string | null;
  completedAt?: string | null;
  /** Mã giao dịch hoàn do VNPay cấp - chỉ có khi đã hoàn thành công. */
  refundTransactionNo?: string | null;
}

export interface RefundStatusResponse {
  orderId: string;
  orderCode: string;
  /** Trạng thái thanh toán của đơn: Unpaid, PartiallyPaid, Paid, Refunded, Failed, PartiallyRefunded, NotFound. */
  paymentStatus: string;
  /** Nhãn do BE tính (dùng chung với Dashboard): "Đã thanh toán", "VNPay"... */
  paymentStatusLabel?: string;
  paymentMethodLabel?: string;
  /** Thẻ/kênh đã dùng khi thanh toán qua VNPay, vd "Thẻ ATM nội địa · NCB". Không bao giờ có số thẻ. */
  paymentInstrument?: string | null;
  /** true = đơn qua cổng, chưa huỷ và chưa có thanh toán thành công - khách có thể "Thanh toán ngay" (BE tạo link mới). */
  canPayNow?: boolean;
  /** false = đơn chưa có lệnh hoàn nào - ẩn khối "Hoàn tiền". */
  hasRefund: boolean;
  status?: RefundPublicStatus | null;
  statusText?: string | null;
  amount?: number | null;
  totalRefunded: number;
  requestedAt?: string | null;
  completedAt?: string | null;
  refundTransactionNo?: string | null;
  estimatedBusinessDays: number;
  items: RefundStatusItem[];
  /** Số tiền dự kiến được hoàn nếu huỷ đơn bây giờ (theo chính sách hiện hành). null khi đơn chưa thanh toán qua cổng. */
  expectedRefundAmount?: number | null;
  /** Chính sách hiện hành có hoàn phí vận chuyển không. */
  refundShippingFee: boolean;
  shippingFee: number;
}

@Injectable({ providedIn: "root" })
export class PaymentApiService {
  private readonly apiUrl = environment.apiURL;
  private readonly apiV = environment.version;

  constructor(private httpClient: HttpClient) {}

  /**
   * Nguồn sự thật về trạng thái thanh toán.
   *
   * SignalR chỉ là kênh tăng tốc: sự kiện được bắn đúng một lần, nên nếu IPN về lúc khách còn đang ở
   * cổng thanh toán hoặc lúc kết nối vừa rớt thì sự kiện đó mất luôn. Mọi màn hình liên quan tới
   * thanh toán đều phải hỏi endpoint này khi khởi tạo và khi kết nối lại.
   */
  /**
   * Sinh link thanh toán mới cho một đơn đã tồn tại - dùng khi khách thử thanh toán lại.
   *
   * Backend tạo một lần thử mới với vnp_TxnRef riêng, nên không đụng vào đơn cũ và không tạo đơn
   * trùng. Đơn đã thanh toán rồi thì backend từ chối.
   */
  createPayment(orderId: string): Observable<CreatePaymentResponse> {
    return this.httpClient
      .post<unknown>(
        `${this.apiUrl}/api/${this.apiV}/Payment/vnpay/create`,
        JSON.stringify(orderId),
        { headers: { "Content-Type": "application/json" } }
      )
      .pipe(map((res) => unwrap<CreatePaymentResponse>(res)));
  }

  /**
   * Ask VNPay (querydr) about the latest pending attempt and settle it if the gateway has concluded.
   * For the "waiting for confirmation" page when the IPN got lost. Outcome: Applied | AlreadyPaid |
   * InProgress | NotFoundAtGateway | RateLimited | GatewayError | NoPendingAttempt | Rejected.
   */
  syncStatus(orderId: string): Observable<PaymentSyncResult> {
    return this.httpClient
      .post<unknown>(`${this.apiUrl}/api/${this.apiV}/Payment/sync`, null, { params: { orderId } })
      .pipe(map((res) => unwrap<PaymentSyncResult>(res)));
  }

  getStatus(orderId: string): Observable<PaymentStatusResponse> {
    return this.httpClient
      .get<unknown>(`${this.apiUrl}/api/${this.apiV}/Payment/check-status`, {
        params: { orderId },
      })
      .pipe(map((res) => unwrap<PaymentStatusResponse>(res)));
  }

  /**
   * Tiền hoàn của đơn đang ở đâu. Chỉ đọc - khách không tự hoàn tiền, chỉ yêu cầu qua việc huỷ đơn.
   * Backend chỉ trả đơn của chính người gọi; đơn người khác nhận paymentStatus = "NotFound".
   */
  getRefundStatus(orderId: string): Observable<RefundStatusResponse> {
    return this.httpClient
      .get<unknown>(`${this.apiUrl}/api/${this.apiV}/Payment/refund-status`, {
        params: { orderId },
      })
      .pipe(map((res) => unwrap<RefundStatusResponse>(res)));
  }
}
